import React from 'react';
import { extractArgs, parseConfig, drilldownToConfig } from '../utils';
import { MergedConfig } from '../utils/dashboardLinkMerge';
import {
  handleUserTimeline,
  handleViewerModule,
  handleSessionTimeline,
  handleNewAppVersion,
  handleBrokenAssets,
  handleAiAlerts,
  handleVSIAiAlerts,
  handleSpiImprovementsRoadmap,
  handleImpactedViewers,
  handleImpactedSessions,
  handleReportView,
} from '../dashboardHandlers';
import {
  getSessionId,
  panelWidthAtom,
  isReportPanelOpenAtom,
  activeReportVersionAtom,
} from '../NexaStore';
import { Button, Tooltip } from 'antd';
import { PicRightOutlined } from '@ant-design/icons';
import { useSetAtom } from 'jotai';
import { useDispatch } from 'react-redux';
import { get } from 'lodash';
import useGetDashboardConfig from '../hooks/useGetDashboardConfig';
import { ActionWrapper } from './styled';
import useCleanupDashboardContext from '../hooks/useCleanupDashboardContext';
import apptracker from '@src/utils/sensor/apptracker';
import { NEXA_DASHBOARD_LINK_CLICK } from '@src/utils/sensor/track';
import { logger } from '@src/utils/misc/logger';

import DashboardIcon from '@icons/nexa/dashboard.svg';

const { baseUrl } = window.conviva;

// Handle click for trends dashboard drilldown
// Used for metrics_query, metric_filter, and flow_query tools
// Note: comparisonInterval (if present) is inside rawConfig.args and extracted by extractArgs
const handleTrendsClick = ({ rawConfig, redirectHandler }) => {
  try {
    // Parse config on demand when user clicks
    // extractArgs will pull out comparisonInterval if present (from merged links)
    const parsedArgs = extractArgs(rawConfig);
    const dashboardConfig = parseConfig(parsedArgs);
    const id = getSessionId();
    drilldownToConfig({ dashboardConfig, id, redirectHandler });
    apptracker(NEXA_DASHBOARD_LINK_CLICK, {
      type: parsedArgs.comparisonInterval ? 'trends-dod' : 'trends',
      id,
      merged: !!parsedArgs.comparisonInterval,
    });
  } catch (error) {
    logger.error('Error generating dashboard config on click:', error);
  }
};

// Configuration mapping tool names to their handler functions
// Each handler is self-contained and executes its own action
// To add a new tool: create a handler in dashboardHandlers/ and add it here
const TOOL_HANDLERS = {
  metrics_query: handleTrendsClick,
  metric_filter: handleTrendsClick,
  flow_query: handleTrendsClick,
  fetch_sessions_csv: handleUserTimeline,
  fetch_event_list_json: handleUserTimeline,
  fetch_viewer_sessions_csv: handleViewerModule,
  fetch_session_timeline_json: handleSessionTimeline,
  fetch_app_versions_csv: handleNewAppVersion,
  fetch_broken_assets_csv: handleBrokenAssets,
  alerts_query: handleAiAlerts,
  video_alerts_diagnose_query: handleVSIAiAlerts,
  video_alerts_summary_query: handleVSIAiAlerts,
  spi_improvements_roadmap: handleSpiImprovementsRoadmap,
  fetch_impacted_viewers_csv: handleImpactedViewers,
  fetch_impacted_sessions_csv: handleImpactedSessions,
  generate_static_report: handleReportView,
};

// Tooltip text for different tool types
const TOOL_TOOLTIPS = {
  metrics_query: 'Analyze on a Dashboard',
  metric_filter: 'Analyze on a Dashboard',
  flow_query: 'Analyze on a Dashboard',
  fetch_sessions_csv: 'Analyze on Timeline Dashboard',
  fetch_event_list_json: 'Analyze on Timeline Dashboard',
  fetch_session_timeline_json: 'Analyze on Timeline Dashboard',
  fetch_viewer_sessions_csv: 'Analyze on Viewer Dashboard',
  fetch_app_versions_csv: 'View New App Versions',
  fetch_broken_assets_csv: 'View Broken Assets',
  alerts_query: 'View Alerts',
  video_alerts_diagnose_query: 'Analyze Video Alert Diagnostics',
  video_alerts_summary_query: 'View Video Alerts',
  spi_improvements_roadmap: 'View AI Roadmap',
  fetch_impacted_viewers_csv: 'View Impacted Viewers',
  fetch_impacted_sessions_csv: 'View Impacted Sessions',
  generate_static_report: 'View Report',
};

const DashboardLink = ({ fnCallConfig }) => {
  const setPanelWidth = useSetAtom(panelWidthAtom);
  const setIsReportPanelOpen = useSetAtom(isReportPanelOpenAtom);
  const setActiveReportVersion = useSetAtom(activeReportVersionAtom);
  const rawConfig = useGetDashboardConfig(fnCallConfig);
  const dispatch = useDispatch();

  const cleanupDashboardContext = useCleanupDashboardContext();

  if (!rawConfig) return null;

  // Check if we have a handler for this tool
  const toolName = get(rawConfig, 'tool_name', null);
  if (!toolName || !TOOL_HANDLERS[toolName]) {
    return null;
  }

  const redirectHandler = ({ id }) => {
    cleanupDashboardContext(id);
    setPanelWidth(30);
  };

  // Execute the appropriate tool handler
  // All handlers now accept a unified options object
  const handleClick = () => {
    const handler = TOOL_HANDLERS[toolName];
    const { args } = rawConfig;

    // Don't close report panel if we're opening a report
    if (toolName !== 'generate_static_report') {
      setIsReportPanelOpen(false);
    }

    // Pass all context as options object for flexibility
    handler({
      args,
      baseUrl,
      dispatch,
      rawConfig,
      redirectHandler,
      setIsReportPanelOpen,
      setActiveReportVersion,
    });
  };

  const tooltipText =
    toolName && TOOL_TOOLTIPS[toolName] ? TOOL_TOOLTIPS[toolName] : 'Analyze on a Dashboard';

  // Use report icon for generate_static_report, dashboard icon for others
  const icon =
    toolName === 'generate_static_report' ? (
      <PicRightOutlined style={{ fontSize: 16, color: '#FFA500' }} />
    ) : (
      <DashboardIcon style={{ fontSize: 24 }} />
    );

  return (
    <ActionWrapper>
      <Tooltip title={tooltipText} placement="topLeft">
        <Button style={{ padding: 0 }} onClick={handleClick} icon={icon} type="text" />
      </Tooltip>
    </ActionWrapper>
  );
};

/**
 * MergedDashboardLink - Renders a dashboard link for merged tool calls
 * Handles same-interval merges, DoD comparison merges, and filter comparison merges
 * Note: comparisonInterval and compareFilters are stored inside mergedConfig.args for self-contained configs
 */
export const MergedDashboardLink = ({ mergedConfig }: { mergedConfig: MergedConfig }) => {
  const setPanelWidth = useSetAtom(panelWidthAtom);
  const setIsReportPanelOpen = useSetAtom(isReportPanelOpenAtom);

  const cleanupDashboardContext = useCleanupDashboardContext();

  const redirectHandler = ({ id }) => {
    cleanupDashboardContext(id);
    setPanelWidth(30);
  };

  const handleClick = () => {
    setIsReportPanelOpen(false);

    // Build a rawConfig-like structure from merged args
    // comparisonInterval and compareFilters are already inside args, so extractArgs will pick them up
    const rawConfig = {
      tool_name: 'metrics_query', // Use metrics_query as the base tool
      args: mergedConfig.args,
    };

    handleTrendsClick({
      rawConfig,
      redirectHandler,
    });
  };

  // Determine tooltip based on merge type
  const getTooltipText = () => {
    switch (mergedConfig.type) {
      case 'dod-merged':
        return 'Analyze with Time Comparison on Dashboard';
      case 'filter-compare-merged':
        return 'Compare Filters on Dashboard';
      default:
        return 'Analyze on a Dashboard';
    }
  };

  return (
    <ActionWrapper>
      <Tooltip title={getTooltipText()} placement="topLeft">
        <Button
          style={{ padding: 0 }}
          onClick={handleClick}
          icon={<DashboardIcon style={{ fontSize: 24 }} />}
          type="text"
        />
      </Tooltip>
    </ActionWrapper>
  );
};

export default DashboardLink;
